import { UIMatch, useMatches } from "react-router";
import { BreadcrumbSchema } from "~/components/BreadcrumbSchema";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";
import { CANONICAL_URL } from "~/utils/constant";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../ui/breadcrumb";
import SortByComponent from "./SortByComponent";
import { useIsMobile } from "~/hook/use-mobile";

interface LoaderData {
  title?: string;
  [key: string]: any;
}

type MatchWithTitle = UIMatch<LoaderData> & {
  handle?: {
    breadcrumb?: (match: MatchWithTitle) => React.ReactNode;
    breadcrumbSchema?: (match: MatchWithTitle) => { name: string; path: string }[];
  };
};

const TitlePreview = ({ showFilterMenu = false }: { showFilterMenu?: boolean }) => {
  const matches = useMatches() as MatchWithTitle[];
  const { bold, medium } = useFontStyle();
  const isMobile = useIsMobile();

  const breadcrumbItems = matches
    .filter((match) => typeof match.handle?.breadcrumb === "function")
    .map((match, index, arr) => {
      const isLast = index === arr.length - 1;

      let crumbNode: React.ReactNode = null;

      try {
        crumbNode = match.handle!.breadcrumb!(match);
      } catch (e) {
        console.warn(`Breadcrumb error in route ${match.id}:`, e);
      }

      return (
        <BreadcrumbItem key={match.id}>
          {isLast ? (
            <BreadcrumbPage>{crumbNode}</BreadcrumbPage>
          ) : (
            <>
              {crumbNode}
              <BreadcrumbSeparator />
            </>
          )}
        </BreadcrumbItem>
      );
    });

  const matchedWithTitle = [...matches].reverse().find((match) => typeof match.data?.title === "string");

  const dynamicTitle = matchedWithTitle?.data?.title ?? "";

  // Build BreadcrumbList schema from the deepest route that defines breadcrumbSchema handle
  const schemaMatch = [...matches].reverse().find((match) => typeof match.handle?.breadcrumbSchema === "function");
  const lang = matches.find((m) => m.params.lang)?.params.lang ?? "en";

  const breadcrumbSchemaItems = (() => {
    if (!schemaMatch) return null;
    let items: { name: string; path: string }[];
    try {
      items = schemaMatch.handle!.breadcrumbSchema!(schemaMatch);
    } catch {
      return null;
    }
    return items.map(({ name, path }) => {
      const trimmedPath = path === "/" ? "" : path?.startsWith("/") ? path : `/${path}`;
      return { name, item: `${CANONICAL_URL}/${lang}${trimmedPath}` };
    });
  })();

  return (
    <div className="w-full flex flex-col flex-wrap gap-2 justify-center items-center px-2 text-center">
      {breadcrumbSchemaItems && breadcrumbSchemaItems.length > 0 && <BreadcrumbSchema items={breadcrumbSchemaItems} />}
      <Breadcrumb className="w-full text-center">
        <BreadcrumbList className={cn("w-full flex flex-wrap gap-1 justify-center items-center text-center", medium)}>
          {breadcrumbItems}
          {breadcrumbItems.length > 0 && (
            <BreadcrumbPage className={cn("text-16 text-primaryColor capitalize ", medium)}>
              {dynamicTitle.length > 20 && isMobile ? `${dynamicTitle.slice(0, 20)}...` : dynamicTitle}
            </BreadcrumbPage>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex justify-center items-center min-w-full relative">
        {dynamicTitle.trim() !== "" && (
          <h1 className={cn("h1_heading text-center text-titlePreviewColor capitalize px-4 sm:px-6 max-w-[90vw] break-words leading-snug", medium)}>
            {dynamicTitle.length > 20 && isMobile ? `${dynamicTitle.slice(0, 20)}...` : dynamicTitle}
          </h1>
        )}

        {showFilterMenu && (
          <div className="absolute right-0 flex justify-end pr-4">
            <SortByComponent />
          </div>
        )}
      </div>
    </div>
  );
};

export default TitlePreview;
